import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	Box,
	Container,
	Paper,
	Typography,
	CircularProgress,
	Alert,
	Grid2,
	Button,
	Chip,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
	setAssignment,
	createDocumentAsync,
	updateAssignmentAsync,
	clearErrors,
	resetDocumentCreation,
} from "../features/AssignmentSlice";
import {
	getDecryptedValue,
	fetchAssignmentsById,
	getOpenSignDocument,
	sendMessage,
	checkDocumentExists,
	uploadAttachment,
} from "../utility/api";
import { formatDisplayDate, numberToWords } from "../utility/utils";
import {
	getSigningUrlForUser,
	matchesSignerName,
	hasSignerSigned,
	getSignerForClaimantLink,
} from "../utility/opensign-helpers";
import OpenNewWindowButton from "./OpenNewWindowButton";

const AcceptanceForm = () => {
	const dispatch = useDispatch();
	const { token } = useParams();
	const navigate = useNavigate();
	const docIdRef = useRef(null);
	const iframeRef = useRef(null);
	const tokenInitializedRef = useRef(false); // Track if token has been processed
	const documentInitializedRef = useRef(false); // Track if document has been initialized// State management
	const [localErrors, setLocalErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [openSignEmbedUrl, setOpenSignEmbedUrl] = useState(null);
	const [openSignDocumentId, setOpenSignDocumentId] = useState(null);
	const [assignmentName, setAssignmentName] = useState(null);
	const [claimantPartyId, setClaimantPartyId] = useState(null);
	const [tokenContextReady, setTokenContextReady] = useState(false);
	const [iframeError, setIframeError] = useState(false);
	const [showNotifyButton, setShowNotifyButton] = useState(false);
	const [showSendForApproval, setShowSendForApproval] = useState(false);
	const [isDownloadingAndUploading, setIsDownloadingAndUploading] =
		useState(false);
	const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
	const [adjustorAlreadySigned, setAdjustorAlreadySigned] = useState(false);
	const [signingStatus, setSigningStatus] = useState(null); // { signers: [{Name, signed, signedAt}], allSigned }
	const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

	// Fetch live signing status for a document
	const fetchSigningStatus = async (docId) => {
		try {
			const docDetails = await getOpenSignDocument(docId);
			if (!docDetails) return null;

			let signers = docDetails.Signers || [];
			const auditTrail = docDetails.AuditTrail || [];

			// Fallback: extract from Placeholders if Signers is empty
			if (signers.length === 0 && docDetails.Placeholders?.length > 0) {
				signers = docDetails.Placeholders.filter(
					(p) => p.signerObjId || p.signerPtr?.objectId,
				)
					.map((p) => ({
						objectId: p.signerObjId || p.signerPtr?.objectId,
						Name: p.Role || p.signerPtr?.Name || "",
						Email: p.signerPtr?.Email || "",
					}))
					.filter(
						(s, i, arr) =>
							arr.findIndex((x) => x.objectId === s.objectId) ===
							i,
					);
			}

			const signerStatuses = signers.map((signer) => {
				const signedEntry = auditTrail.find(
					(entry) => hasSignerSigned(signer, [entry]),
				);
				return {
					Name: signer.Name || "Unknown",
					signed: !!signedEntry,
					signedAt:
						signedEntry?.updatedAt ||
						signedEntry?.createdAt ||
						null,
				};
			});

			const allSigned =
				signerStatuses.length > 0 &&
				signerStatuses.every((s) => s.signed);

			return {
				signers: signerStatuses,
				allSigned,
				prematurelyCompleted:
					docDetails.IsCompleted === true && !allSigned,
				lastRefreshed: new Date(),
				hasSignedUrl: !!docDetails.SignedUrl,
			};
		} catch (error) {
			console.error("Error fetching signing status:", error);
			return null;
		}
	};

	const handleRefreshStatus = async () => {
		if (!openSignDocumentId || isRefreshingStatus) return;
		setIsRefreshingStatus(true);
		try {
			const status = await fetchSigningStatus(openSignDocumentId);
			if (status) setSigningStatus(status);
		} finally {
			setIsRefreshingStatus(false);
		}
	};

	const notifyUser = async () => {
		try {
			setIsSubmitting(true);

			// Check if claimants array exists and has at least one claimant
			if (
				!assignmentFromStore?.claimants ||
				assignmentFromStore.claimants.length === 0
			) {
				alert("No claimant information available");
				return;
			}

			// Send message to all claimants
			const sendPromises = assignmentFromStore.claimants.map(
				async (claimant, index) => {
					try {
						if (!claimant.partyId) {
							console.warn(
								`Claimant ${
									index + 1
								} has no partyId, skipping...`,
							);
							return {
								success: false,
								claimant: index + 1,
								error: "No partyId",
							};
						}

						await sendMessage(
							assignmentFromStore.assignmentName,
							"DVForm",
							claimant.partyId,
						);

						return {
							success: true,
							claimant: index + 1,
							partyId: claimant.partyId,
						};
					} catch (error) {
						console.error(
							`Failed to send message to claimant ${index + 1}:`,
							error,
						);
						return {
							success: false,
							claimant: index + 1,
							error: error.message,
						};
					}
				},
			);

			// Wait for all messages to be sent
			const results = await Promise.all(sendPromises);

			// Count successful and failed sends
			const successful = results.filter((r) => r.success).length;
			const failed = results.filter((r) => !r.success).length;

			if (successful > 0 && failed === 0) {
				alert(
					`Acceptance Form shared successfully to all ${successful} claimant(s) via WhatsApp`,
				);
			} else if (successful > 0 && failed > 0) {
				alert(
					`Acceptance Form shared to ${successful} claimant(s) via WhatsApp. ${failed} failed to send.`,
				);
				console.warn(
					"Failed sends:",
					results.filter((r) => !r.success),
				);
			} else {
				throw new Error(
					"Failed to send notifications to any claimants",
				);
			}

			navigate("/home");
		} catch (error) {
			console.error("Error sending notifications:", error);
			alert("Failed to send notifications. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderAssignmentDetails = (assignment) => {
		if (!assignment) return null;

		const InfoField = ({ label, value, size = 6, multiline = false }) => {
			const normalizeValue = (val) => {
				if (val === null || val === undefined || val === "")
					return "N/A";
				if (typeof val === "object") {
					// Handle date-like objects { day, month, year }
					if (
						Object.prototype.hasOwnProperty.call(val, "day") &&
						Object.prototype.hasOwnProperty.call(val, "month") &&
						Object.prototype.hasOwnProperty.call(val, "year")
					) {
						const d = String(val.day).padStart(2, "0");
						const m = String(val.month).padStart(2, "0");
						const y = String(val.year);
						return `${d}/${m}/${y}`;
					}
					try {
						return JSON.stringify(val);
					} catch {
						return String(val);
					}
				}
				return String(val);
			};

			return (
				<Grid2 size={size}>
					<Box
						sx={{
							p: 1.5,
							bgcolor: "white",
							borderRadius: 1,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
								display: "block",
								mb: 0.5,
							}}
						>
							{label}
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{normalizeValue(value)}
						</Typography>
					</Box>
				</Grid2>
			);
		};

		return (
			<Box sx={{ p: 1 }}>
				<Typography
					variant="h6"
					sx={{
						mb: 2,
						color: "primary.main",
						fontWeight: "medium",
					}}
				>
					Basic Information
				</Typography>

				{/* Assignment Details */}
				<Box sx={{ mb: 2 }}>
					<Typography
						variant="subtitle2"
						sx={{
							mb: 2,
							color: "text.primary",
						}}
					>
						Assignment Details
					</Typography>
					<Grid2 container spacing={1}>
						<InfoField
							label="Claim Number"
							value={assignment.claimNumber}
							size={6}
						/>
						<InfoField
							label="Policy Number"
							value={assignment.policyNumber}
							size={6}
						/>
						<InfoField
							label="Insurance Company"
							value={assignment.insuranceCompany}
							size={12}
						/>
						<InfoField
							label="Type"
							value={assignment.type}
							size={6}
						/>
						<InfoField
							label="Nature of Loss"
							value={assignment.natureOfLoss}
							size={6}
						/>
					</Grid2>
				</Box>

				{/* Important Dates */}
				<Box sx={{ mb: 2 }}>
					<Typography
						variant="subtitle2"
						sx={{
							mb: 2,
							color: "text.primary",
						}}
					>
						Important Dates
					</Typography>
					<Grid2 container spacing={1}>
						<InfoField
							label="Date of Loss"
							value={formatDisplayDate(assignment.dateOfLoss)}
							size={4}
						/>
						<InfoField
							label="Assignment Received"
							value={formatDisplayDate(
								assignment.assignmentReceived,
							)}
							size={4}
						/>
						<InfoField
							label="Date of Survey"
							value={formatDisplayDate(assignment.dateOfSurvey)}
							size={4}
						/>
					</Grid2>
				</Box>

				{/* Contact Information */}
				<Box sx={{ mb: 2 }}>
					<Typography
						variant="subtitle2"
						sx={{
							mb: 2,
							color: "text.primary",
						}}
					>
						Contact Information
					</Typography>
					<Grid2 container spacing={1}>
						{assignment.claimants.map((insuredInfo, index) => (
							<React.Fragment key={index}>
								<InfoField
									label={`${
										insuredInfo.isPrimary ? "Primary-" : ""
									} Insured Name`}
									value={insuredInfo.insuredName}
									size={6}
								/>
								<InfoField
									label={`${
										insuredInfo.isPrimary ? "Primary-" : ""
									} Insured Contact`}
									value={insuredInfo.insuredContactDetails}
									size={6}
								/>
							</React.Fragment>
						))}

						<InfoField
							label="Address"
							value={assignment.address}
							size={12}
							multiline
						/>
						<InfoField
							label="Broker/Agent Details"
							value={assignment.brokersOrInsurerInvolved}
							size={6}
						/>
						<InfoField
							label="Settled Amount"
							value={assignment.settledAmount}
							size={6}
						/>
					</Grid2>
				</Box>
			</Box>
		);
	};

	const downloadAndUpload = async () => {
		try {
			setIsDownloadingAndUploading(true);
			setLocalErrors({});

			if (!openSignDocumentId || !assignmentFromStore.assignmentName) {
				alert("Document information not available");
				return;
			}

			// Get document details to access the SignedUrl
			const docDetails = await getOpenSignDocument(openSignDocumentId);

			if (!docDetails) {
				throw new Error("Could not fetch document details");
			}

			// Guard: every signer (adjuster, claimant(s), witness) must have
			// signed before the form can be sent for approval.
			const status = await fetchSigningStatus(openSignDocumentId);
			if (status) setSigningStatus(status);
			if (!status?.allSigned) {
				alert(
					"All parties must sign the document before it can be sent for approval.",
				);
				return;
			}

			// Check if document has a SignedUrl
			const signedUrl = docDetails.SignedUrl;
			if (!signedUrl) {
				throw new Error(
					"Document does not have a signed URL. Please ensure the document is fully signed.",
				);
			}

			// Download PDF directly from the SignedUrl
			const pdfResponse = await fetch(signedUrl);

			if (!pdfResponse.ok) {
				throw new Error("Failed to download PDF from signed URL");
			}

			const pdfBlob = await pdfResponse.blob();

			// Create FormData for upload
			const formData = new FormData();
			const fileName = `${assignmentFromStore.assignmentName}_signed_acceptance_form.pdf`;
			formData.append("images", pdfBlob, fileName);
			formData.append(
				"assignmentName",
				assignmentFromStore.assignmentName,
			);
			formData.append(
				"AttachmentDetails",
				JSON.stringify([
					{
						FileName: fileName,
					},
				]),
			);

			// Upload to eMacsUI
			const uploadResult = await uploadAttachment(formData);

			if (uploadResult) {
				// Update assignment status to "Review" so the Manager can approve it
				try {
					const { updateAssignmentStatus } = await import("../utility/api");
					await updateAssignmentStatus(
						assignmentFromStore.assignmentName,
						"Review",
						effectiveUser,
					);
				} catch (statusError) {
					console.error(
						"Failed to update assignment status to Review:",
						statusError,
					);
				}

				alert(
					"Acceptance Form successfully uploaded and sent for review!",
				);
				navigate("/home");
			}
		} catch (error) {
			console.error("Error in download and upload:", error);
			setLocalErrors({
				downloadUpload:
					error.message || "Failed to download and upload PDF",
			});
			alert("Failed to download and upload PDF. Please try again.");
		} finally {
			setIsDownloadingAndUploading(false);
		}
	};

	// Allow Adjuster to (re)create document from template on demand
	const reinitializeDocumentFromTemplate = async () => {
		try {
			if (
				!effectiveUser?.userName ||
				!effectiveUser?.role ||
				!/adjuster/i.test(effectiveUser.role)
			) {
				alert(
					"Only internal Adjusters can create the acceptance form.",
				);
				return;
			}

			if (!assignmentFromStore) {
				alert("Assignment is not loaded yet.");
				return;
			}

			const { templateId } = assignmentFromStore;
			if (!templateId) {
				setLocalErrors((prev) => ({
					...prev,
					template:
						"No template configured for this assignment. Please set up a template first.",
				}));
				return;
			}

			setIsCreatingFromTemplate(true);
			setLocalErrors((prev) => ({
				...prev,
				template: undefined,
				access: undefined,
			}));
			docIdRef.current = "creating"; // guard against duplicate creations

			const currentAssignmentName =
				assignmentName || assignmentFromStore.assignmentName;
			const documentName = `Acceptance Form - ${currentAssignmentName}`;

			const documentMetadata = {
				assignmentName: assignmentFromStore.assignmentName,
				insuranceCompany: assignmentFromStore.insuranceCompany,
				claimants: assignmentFromStore.claimants,
				adjuster: effectiveUser,
				address: assignmentFromStore.address,
				policyNumber: assignmentFromStore.policyNumber,
				claimNumber: assignmentFromStore.claimNumber,
				natureOfLoss: assignmentFromStore.natureOfLoss,
				dateOfLoss: formatDisplayDate(assignmentFromStore.dateOfLoss),
				situationOfLoss: assignmentFromStore.situationOfLoss,
				settledAmount:
					assignmentFromStore.settledAmount ||
					assignmentFromStore.claimAmount,
				settledAmountInWords: `${numberToWords(
					assignmentFromStore.settledAmount ||
						assignmentFromStore.claimAmount,
				)} Ringgit Malaysia Only`,
			};

			const resultAction = await dispatch(
				createDocumentAsync({
					templateId,
					documentName,
					metadata: documentMetadata,
				}),
			);

			if (createDocumentAsync.fulfilled.match(resultAction)) {
				const newDocId = resultAction.payload.documentId;
				docIdRef.current = newDocId;

				// Persist documentId on the assignment
				dispatch(
					updateAssignmentAsync({
						assignmentId: assignmentFromStore.id,
						documentId: newDocId,
					}),
				);

				// Finalize and load URL for Adjuster
				try {
					const { ensureDocumentIsFinalized } =
						await import("../utility/opensign-helpers");
					await ensureDocumentIsFinalized(newDocId);
					const signingUrl = await getSigningUrlForUser(
						newDocId,
						effectiveUser,
						"Adjustor",
					);
					if (signingUrl) {
						setOpenSignEmbedUrl(signingUrl);
						setOpenSignDocumentId(newDocId);
						// Do not enable the Notify button here: the adjuster has
						// only just opened their own signing page and has not
						// signed yet. Notify becomes available in the post-sign
						// status view (once the adjuster has signed).
						setLoading(false);
						return;
					}
					throw new Error("No signing URL returned after creation");
				} catch (e) {
					setLocalErrors((prev) => ({
						...prev,
						signing:
							"Created document but failed to load signing URL: " +
							e.message,
					}));
				}
			} else {
				setLocalErrors((prev) => ({
					...prev,
					init: "Failed to create document from template.",
				}));
			}
		} catch (error) {
			console.error("Re-initialize document failed:", error);
			setLocalErrors((prev) => ({
				...prev,
				init: "Failed to create document: " + error.message,
			}));
		} finally {
			setIsCreatingFromTemplate(false);
			if (!openSignEmbedUrl) setLoading(false);
		}
	};

	// Redux state
	const user = useSelector((state) => state.user?.user);
	const assignmentFromStore = useSelector(
		(state) => state.assignment?.assignment,
	);
	const documentCreationState = useSelector(
		(state) => state.assignment?.documentCreation,
	) || { loading: false, error: null };
	const documentCreationLoading = documentCreationState.loading;
	const documentCreationError = documentCreationState.error;
	const assignmentError = useSelector((state) => state.assignment?.error);

	// Access mode detection:
	// Adjustor navigates via ExpandableList button which passes { state: { isAdjustor: true } }.
	// Claimant opens a direct link (no navigation state) → always claimant view.
	const location = useLocation();
	const isTokenAccess = !location.state?.isAdjustor;
	const effectiveUser = isTokenAccess ? null : user;

	// Clear any previous errors when component mounts
	useEffect(() => {
		dispatch(clearErrors());

		// Safety timeout to prevent infinite loading
		const loadingTimeout = setTimeout(() => {
			if (loading) {
				console.warn(
					"Safety timeout triggered - forcing loading state to false",
				);
				setLoading(false);
				setLocalErrors((prev) => ({
					...prev,
					timeout:
						"Loading timed out. Please try refreshing the page.",
				}));
			}
		}, 30000); // 30 second maximum loading time

		return () => clearTimeout(loadingTimeout);
	}, [dispatch, loading]);

	// Token decryption and initialization for external access
	useEffect(() => {
		// Reset initialization flag when token changes
		if (token) {
			tokenInitializedRef.current = false;
			documentInitializedRef.current = false;
			docIdRef.current = null;
			setTokenContextReady(false);
			setClaimantPartyId(null);
			setOpenSignEmbedUrl(null);
			setOpenSignDocumentId(null);
			setAdjustorAlreadySigned(false);
			setSigningStatus(null);
			setLoading(true);
		}

		// Prevent multiple calls by checking if we already processed this token
		// For claimant (isTokenAccess), always fetch fresh even if stale store data exists
		if (
			!token ||
			tokenInitializedRef.current ||
			(!isTokenAccess && assignmentFromStore)
		) {
			return;
		}

		const initializeFromToken = async () => {
			tokenInitializedRef.current = true; // Mark as initialized

			// Only decrypt token if:
			// 1. There is a token in the URL
			// 2. Token looks like an encrypted value (longer than a simple assignment name)
			if (token.length > 20) {
				try {
					const decryptedAssignmentName =
						await getDecryptedValue(token);
					if (decryptedAssignmentName) {
						if (decryptedAssignmentName.status === "EXPIRED") {
							throw new Error("This signing link has expired.");
						}
						setAssignmentName(
							decryptedAssignmentName.assignmentName,
						);
						setClaimantPartyId(
							decryptedAssignmentName.partyId || null,
						);

						const response = await fetchAssignmentsById(
							decryptedAssignmentName.assignmentName,
						);
						if (response) {
							dispatch(setAssignment(response));
							setTokenContextReady(true);
						} else {
							console.warn("No data in API response:", response);
						}
					}
				} catch (error) {
					setLocalErrors((prev) => ({
						...prev,
						token:
							"Failed to decrypt token or fetch assignment: " +
							error.message,
					}));
					setLoading(false);
				}
			} else if (token.length <= 20) {
				// If token is short, it might be a plain assignment name, try to fetch directly
				try {
					setAssignmentName(token);
					const response = await fetchAssignmentsById(token);
					if (response) {
						dispatch(setAssignment(response));
						setTokenContextReady(true);
					}
				} catch (error) {
					// If direct fetch fails, try decryption as fallback
					try {
						const decryptedAssignmentName =
							await getDecryptedValue(token);
						if (decryptedAssignmentName) {
							const decryptedName =
								decryptedAssignmentName.assignmentName ||
								decryptedAssignmentName;
							setAssignmentName(decryptedName);
							setClaimantPartyId(
								decryptedAssignmentName.partyId || null,
							);
							const response = await fetchAssignmentsById(
								decryptedName,
							);
							if (response) {
								dispatch(setAssignment(response));
								setTokenContextReady(true);
							}
						}
					} catch (decryptError) {
						setLocalErrors((prev) => ({
							...prev,
							token:
								"Failed to fetch assignment: " + error.message,
						}));
						setLoading(false);
					}
				}
			}
		};

		// Only run token initialization once per token
		initializeFromToken();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, dispatch]); // assignmentFromStore intentionally excluded to prevent infinite loops	// Document initialization for both internal and external users
	useEffect(() => {
		const initializeDocument = async () => {
			// For internal users, require username
			// For external users (token access), allow without username
			if (!effectiveUser?.userName && !token) {
				return;
			}
			if (isTokenAccess && !tokenContextReady) {
				return;
			}

			try {
				if (!assignmentFromStore) {
					// For external users with token, assignment might still be loading
					if (!effectiveUser?.userName && token) {
						// Set loading only if it's not already set
						if (!loading) {
							setLoading(true);
						}
						// Add a timeout to prevent infinite loading
						setTimeout(() => {
							if (loading && !assignmentFromStore) {
								console.warn(
									"Loading timeout - no assignment data received",
								);
								setLoading(false);
								setLocalErrors((prev) => ({
									...prev,
									timeout:
										"Loading timeout - could not retrieve assignment data. Please refresh the page.",
								}));
							}
						}, 15000); // 15 second timeout
						return;
					}
					// Only show error for internal users
					if (effectiveUser?.userName) {
						setLocalErrors((prev) => ({
							...prev,
							init: "Please select an assignment first",
						}));
					}
					setLoading(false);
					return;
				}

				// Check if we already have a document or are creating one
				if (
					docIdRef.current === "creating" ||
					(assignmentFromStore?.openSignDocumentId &&
						openSignEmbedUrl)
				) {
					return;
				}

				const { templateId, openSignDocumentId } = assignmentFromStore;
				let docId = openSignDocumentId; // If we already have a document ID, use it
				if (docId) {
					docIdRef.current = docId;

					// Clear any stale documentCreation loading from previous operations
					dispatch(resetDocumentCreation());

					await loadDocumentUrl(docId, effectiveUser);
					return;
				} // Check for existing documents for both internal and external users
				// For external users, we should check for existing documents even without templateId

				if (
					!docIdRef.current &&
					(templateId || !effectiveUser?.userName)
				) {
					const currentAssignmentName =
						assignmentName || assignmentFromStore.assignmentName;
					const documentName = `Acceptance Form - ${currentAssignmentName}`;

					// Legacy fallback: assignments created before OPENSIGNDOCUMENTID
					// was persisted reach this path. The name-based search is
					// non-deterministic across OpenSign report types and can race —
					// new assignments should hit the openSignDocumentId branch above.
					console.warn(
						`No persisted openSignDocumentId for assignment "${currentAssignmentName}" — falling back to name-based lookup. This path should only fire for legacy records.`,
					);
					const existingDocument =
						await checkDocumentExists(documentName);

					if (existingDocument && existingDocument.objectId) {
						// Use the existing document's objectId
						docId = existingDocument.objectId;
						docIdRef.current = docId;

						// Clear any stale documentCreation loading from previous operations
						dispatch(resetDocumentCreation());

						// Update assignment with existing document ID (only for internal users)
						if (effectiveUser?.userName) {
							dispatch(
								updateAssignmentAsync({
									assignmentId: assignmentFromStore.id,
									documentId: docId,
								}),
							);
						} // Load the document URL for the existing document
						await loadDocumentUrl(docId, effectiveUser);
						return;
					}
				} // If we don't have a document ID but have a template ID, create a new document
				// Only internal users can create documents
				if (
					templateId &&
					!docIdRef.current &&
					effectiveUser?.userName
				) {
					setLoading(true);
					try {
						// Set a flag to prevent multiple document creations
						docIdRef.current = "creating";
						const currentAssignmentName =
							assignmentName ||
							assignmentFromStore.assignmentName;
						const documentName = `Acceptance Form - ${currentAssignmentName}`; // Create comprehensive metadata with all fields needed for the form
						const documentMetadata = {
							assignmentName: assignmentFromStore.assignmentName,
							insuranceCompany:
								assignmentFromStore.insuranceCompany,
							claimants: assignmentFromStore.claimants,
							adjuster: effectiveUser,
							// Add fields required for document placeholders
							address: assignmentFromStore.address,
							policyNumber: assignmentFromStore.policyNumber,
							claimNumber: assignmentFromStore.claimNumber,
							natureOfLoss: assignmentFromStore.natureOfLoss,
							dateOfLoss: formatDisplayDate(
								assignmentFromStore.dateOfLoss,
							),
							situationOfLoss:
								assignmentFromStore.situationOfLoss,
							// Handle fields that might have different names or need transformation
							settledAmount:
								assignmentFromStore.settledAmount ||
								assignmentFromStore.claimAmount,
							settledAmountInWords: `${numberToWords(
								assignmentFromStore.settledAmount ||
									assignmentFromStore.claimAmount,
							)} Ringgit Malaysia Only`,
						};

						// Log metadata for debugging
						console.log(
							"Document metadata for form fields:",
							documentMetadata,
						);

						// Dispatch create document action

						const resultAction = await dispatch(
							createDocumentAsync({
								templateId,
								documentName,
								metadata: documentMetadata,
							}),
						);

						if (createDocumentAsync.fulfilled.match(resultAction)) {
							docId = resultAction.payload.documentId;
							docIdRef.current = docId;

							// Update assignment with new document ID
							dispatch(
								updateAssignmentAsync({
									assignmentId: assignmentFromStore.id,
									documentId: docId,
								}),
							);

							// Load the document URL
							await loadDocumentUrl(docId, effectiveUser);
						}
					} catch (error) {
						setLocalErrors((prev) => ({
							...prev,
							init:
								"Failed to process document creation: " +
								error.message,
						}));
						docIdRef.current = null;
						setLoading(false);
					}
				} else if (
					!effectiveUser?.userName &&
					token &&
					!docIdRef.current
				) {
					// External token users who don't find an existing document
					console.warn(
						`External user attempted to access assignment ${
							assignmentName || assignmentFromStore.assignmentName
						} but no existing document was found.`,
					);
					setLocalErrors((prev) => ({
						...prev,
						access: "No acceptance form document found for this assignment. The document may not have been created yet or the link may be invalid. Please contact the administrator or check back later.",
					}));
					setLoading(false);
				} else if (
					!templateId &&
					!docIdRef.current &&
					effectiveUser?.userName
				) {
					// Internal users without templateId cannot create documents
					console.warn(
						`Internal user attempted to access assignment ${
							assignmentName || assignmentFromStore.assignmentName
						} but no templateId is available and no existing document was found.`,
					);
					setLocalErrors((prev) => ({
						...prev,
						template:
							"This assignment does not have a template configured for document creation. Please contact the administrator to set up the template.",
					}));
					setLoading(false);
				} else {
					setLoading(false);
				}
			} catch (error) {
				setLocalErrors((prev) => ({
					...prev,
					general: "Failed to initialize document: " + error.message,
				}));
				setLoading(false);
			}
		};
		// Helper function to determine the correct signer based on role and conditions
		const determineSignerName = async (docId, currentUser, token) => {
			try {
				const docDetails = await getOpenSignDocument(docId);

				// Check if adjustor has already signed (document has SignedUrl)
				if (
					docDetails.SignedUrl &&
					currentUser?.role === "Adjuster" &&
					!isTokenAccess
				) {
					setShowNotifyButton(false);
					setShowSendForApproval(true);
				}

				let signers = docDetails?.Signers || [];
				const auditTrail = docDetails?.AuditTrail || [];

				// Fallback: if Signers is empty, extract signer info from Placeholders
				if (
					signers.length === 0 &&
					docDetails?.Placeholders?.length > 0
				) {
					signers = docDetails.Placeholders.filter(
						(p) => p.signerObjId || p.signerPtr?.objectId,
					)
						.map((p) => ({
							objectId: p.signerObjId || p.signerPtr?.objectId,
							Name: p.Role || p.signerPtr?.Name || "",
							Email: p.signerPtr?.Email || "",
						}))
						.filter(
							(s, i, arr) =>
								arr.findIndex(
									(x) => x.objectId === s.objectId,
								) === i,
						);
					console.log(
						"Signers extracted from Placeholders:",
						signers,
					);
				}

				console.log(
					"determineSignerName — signers:",
					signers.map((s) => s.Name),
					"signed:",
					auditTrail
						.filter((e) => e.Activity === "Signed")
						.map((e) => e.UserPtr?.Name),
				);

				if (isTokenAccess && claimantPartyId) {
					const claimantLinkSigner = getSignerForClaimantLink(
						docDetails,
						assignmentFromStore?.claimants || [],
						claimantPartyId,
					);
					if (!claimantLinkSigner) {
						console.log(
							"The claimant link has no remaining permitted signer.",
						);
						return null;
					}
					return claimantLinkSigner;
				}

				// For authenticated eMacs Adjustors: if the adjustor signer hasn't signed, pick them
				// For authenticated adjustors: ONLY return their own signer.
				// If they've already signed, return null — do NOT fall through to other signers.
				if (
					currentUser?.userName &&
					!isTokenAccess &&
					/adjuster|adjustor/i.test(currentUser?.role || "")
				) {
					const adjustorSigner = signers.find(
						(s) =>
							matchesSignerName(s.Name, "Adjustor") ||
							matchesSignerName(s.Name, "Adjuster"),
					);
					if (
						adjustorSigner &&
						!hasSignerSigned(adjustorSigner, auditTrail)
					) {
						console.log(
							`Adjustor signer found and unsigned: ${adjustorSigner.Name}`,
						);
						return adjustorSigner;
					}
					// Adjustor has already signed — return null to prevent signing as another role
					console.log(
						"Authenticated adjustor has already signed. Not providing another signer's URL.",
					);
					return null;
				}

				// Below logic is for token/external users or non-adjustor roles only
				// Sequential: find the next unsigned signer in order
				const signingOrder = [
					"Adjustor",
					"Adjuster",
					"Claimant",
					"Witness",
				];
				for (const roleName of signingOrder) {
					const signer = signers.find((s) =>
						matchesSignerName(s.Name, roleName),
					);
					if (signer && !hasSignerSigned(signer, auditTrail)) {
						console.log(
							`Next unsigned signer in order: ${signer.Name}`,
						);
						return signer;
					}
				}

				// Any unsigned signer at all
				for (const signer of signers) {
					if (!hasSignerSigned(signer, auditTrail)) {
						console.log(
							`Found other unsigned signer: ${signer.Name}`,
						);
						return signer;
					}
				}

				// All signed — return null to signal completion (don't send anyone to a signing page)
				console.log("All signers have completed signing");
				return null;
			} catch (error) {
				console.error("Error determining signer name:", error);
				throw error;
			}
		}; // Helper function to load document URL
		const loadDocumentUrl = async (docId, currentUser) => {
			try {
				// Import the document finalization function
				const { ensureDocumentIsFinalized } =
					await import("../utility/opensign-helpers");
				// Ensure the document is finalized before trying to get signing URL
				await ensureDocumentIsFinalized(docId); // Determine the appropriate signer name based on conditions
				const signer = await determineSignerName(
					docId,
					currentUser,
				);

				// If signerName is null, either the authenticated adjustor has already signed
				// or ALL signers have completed — show the post-sign status view.
				if (signer === null) {
					console.log(
						"Signer resolved to null (adjustor signed or all complete). Showing post-sign view.",
					);
					setAdjustorAlreadySigned(true);
					setOpenSignDocumentId(docId);
					setShowNotifyButton(
						!!currentUser?.userName && !isTokenAccess,
					);
					setShowSendForApproval(
						!!currentUser?.userName && !isTokenAccess,
					);
					// Fetch initial signing status
					const status = await fetchSigningStatus(docId);
					if (status) setSigningStatus(status);
					setLoading(false);
					return;
				}

				const isTokenAccessForSigning =
					isTokenAccess || (!currentUser?.userName && !!token);
				const signerName = signer.Name || signer.Role || "Claimant";

				// For external users (token access), create a user object with the determined role
				let userForSigning = currentUser;
				if (isTokenAccessForSigning) {
					userForSigning = { role: signerName };
				}

				// Now get the signing URL with the specific signer name
				const signingUrl = await getSigningUrlForUser(
					docId,
					userForSigning,
					signerName,
					isTokenAccessForSigning,
					signer.objectId || null,
				);

				// signingUrl can be null when adjustor already signed or all signers completed
				if (signingUrl === null) {
					console.log(
						"getSigningUrlForUser returned null — all required signing is complete.",
					);
					setAdjustorAlreadySigned(true);
					setOpenSignDocumentId(docId);
					setShowNotifyButton(
						!!currentUser?.userName && !isTokenAccess,
					);
					setShowSendForApproval(
						!!currentUser?.userName && !isTokenAccess,
					);
					// Fetch initial signing status
					const status = await fetchSigningStatus(docId);
					if (status) setSigningStatus(status);
					setLoading(false);
					return;
				}

				if (signingUrl) {
					setOpenSignEmbedUrl(signingUrl);
					setOpenSignDocumentId(docId);
					// Notify is intentionally NOT enabled here. A non-null
					// signing URL means the adjuster still has to sign their own
					// section. They can only notify claimants after they've
					// signed (handled in the post-sign status view above).
					setLoading(false);
				} else {
					throw new Error("No signing URL generated");
				}
			} catch (error) {
				setLocalErrors((prev) => ({
					...prev,
					signing:
						"Failed to get document signing URL: " + error.message,
				}));
				setLoading(false);
			}
		}; // Only run if we have assignment data and either:
		// - Internal user with username, OR
		// - External access via token (even if assignment not loaded yet)		// Also prevent multiple initializations using ref
		if (
			(!isTokenAccess || tokenContextReady) &&
			!openSignEmbedUrl &&
			!documentInitializedRef.current &&
			((assignmentFromStore && (effectiveUser?.userName || token)) ||
				(!effectiveUser?.userName && token && !assignmentFromStore))
		) {
			// Only mark as fully initialized when we have assignment data.
			// For claimant token access, assignment may still be loading;
			// keep the ref false so this effect re-runs once the data arrives.
			if (assignmentFromStore) {
				documentInitializedRef.current = true;
			}
			initializeDocument().catch((error) => {
				console.error("Error during document initialization:", error);
				setLocalErrors((prev) => ({
					...prev,
					initialization:
						"Failed to initialize document: " + error.message,
				}));
				setLoading(false); // Ensure loading is turned off on error
			});
		} else if (loading && openSignEmbedUrl) {
			// Make sure loading is turned off if we have a URL
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		effectiveUser?.userName, // Only track username, not the entire user object
		assignmentFromStore?.assignmentName, // Only track assignment name, not the entire object
		openSignEmbedUrl,
		token,
		tokenContextReady,
		claimantPartyId,
	]); // Reduced dependencies to prevent unnecessary re-runs

	// Handle iframe events
	const handleIframeLoad = () => {
		setIframeError(false);
	};

	const handleIframeError = () => {
		setIframeError(true);
	};

	// Clear iframe error when URL changes
	useEffect(() => {
		if (openSignEmbedUrl) {
			setIframeError(false);
		}
	}, [openSignEmbedUrl]);

	// Poll signing status when adjustor has already signed (live tracking)
	useEffect(() => {
		if (!adjustorAlreadySigned || !openSignDocumentId) return;
		// Don't poll if all signers have completed
		if (signingStatus?.allSigned) return;

		const pollInterval = setInterval(async () => {
			try {
				const status = await fetchSigningStatus(openSignDocumentId);
				if (status) setSigningStatus(status);
			} catch (e) {
				console.warn("Signing status poll failed:", e.message);
			}
		}, 30000); // Poll every 30 seconds

		return () => clearInterval(pollInterval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adjustorAlreadySigned, openSignDocumentId, signingStatus?.allSigned]);

	// Loading timer state
	const [loadingTime, setLoadingTime] = useState(0);

	// Loading timer effect
	const isEffectivelyLoading =
		(loading || documentCreationLoading) &&
		!openSignEmbedUrl &&
		!adjustorAlreadySigned;
	useEffect(() => {
		let timer;
		if (isEffectivelyLoading) {
			timer = setInterval(() => {
				setLoadingTime((prev) => prev + 1);
			}, 1000);
		} else {
			setLoadingTime(0);
		}
		return () => clearInterval(timer);
	}, [isEffectivelyLoading]);

	// Render errors
	const renderErrors = () => {
		const allErrors = [];

		if (documentCreationError) {
			allErrors.push(
				<Alert severity="error" key="docCreation">
					{documentCreationError}
				</Alert>,
			);
		}

		if (assignmentError) {
			allErrors.push(
				<Alert severity="error" key="assignment">
					{assignmentError}
				</Alert>,
			);
		}

		if (iframeError) {
			allErrors.push(
				<Alert severity="warning" key="iframe">
					The document viewer could not be loaded. Please try opening
					the document in a new window.
				</Alert>,
			);
		}

		Object.entries(localErrors).forEach(([key, error]) => {
			if (key === "token") {
				allErrors.unshift(
					<Alert severity="error" key={key}>
						{error}
					</Alert>,
				);
			} else {
				allErrors.push(
					<Alert severity="error" key={key}>
						{error}
					</Alert>,
				);
			}
		});

		return (
			allErrors.length > 0 && <Box sx={{ mt: 2, mb: 2 }}>{allErrors}</Box>
		);
	};

	// Loading indicator — skip if document is already resolved
	if (
		(loading || documentCreationLoading) &&
		!openSignEmbedUrl &&
		!adjustorAlreadySigned
	) {
		return (
			<Container>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						minHeight: "300px",
						color: "white",
					}}
				>
					<CircularProgress size={60} thickness={4} />
					<Typography variant="h6" sx={{ mt: 3 }}>
						{documentCreationLoading
							? "Creating document..."
							: "Loading document..."}
					</Typography>
					<Typography
						variant="body2"
						color="textSecondary"
						sx={{ mt: 1, color: "white" }}
					>
						This may take a few moments ({loadingTime}s)
					</Typography>

					{/* Show refresh button if loading takes too long */}
					{loadingTime > 15 && (
						<Button
							variant="outlined"
							sx={{ mt: 3 }}
							onClick={() => window.location.reload()}
						>
							Refresh Page
						</Button>
					)}
				</Box>
			</Container>
		);
	}

	return (
		<Container>
			<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
				{renderErrors()}

				{/* Document status information */}
				<Box
					sx={{
						mb: 2,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box>
						<Typography variant="h6">
							{assignmentFromStore?.assignmentName ||
								"Acceptance Form"}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Document ID: {openSignDocumentId || "Not available"}
						</Typography>
					</Box>{" "}
					{openSignEmbedUrl && (
						<Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
							{!isTokenAccess && (
								<Button
									variant="contained"
									color="warning"
									onClick={() => navigate("/home")}
								>
									Back
								</Button>
							)}
							<OpenNewWindowButton url={openSignEmbedUrl} />
							{showNotifyButton && (
								<Button
									variant="contained"
									color="success"
									onClick={notifyUser}
									startIcon={<WhatsAppIcon />}
									disabled={isSubmitting}
								>
									Notify via WhatsApp
								</Button>
							)}
							{showSendForApproval && (
								<Button
									variant="contained"
									color="primary"
									onClick={downloadAndUpload}
									startIcon={<CloudDownloadIcon />}
									disabled={
										isDownloadingAndUploading ||
										!openSignDocumentId ||
										!signingStatus?.allSigned
									}
								>
									{isDownloadingAndUploading
										? "Processing..."
										: "Send for Approval"}
								</Button>
							)}
						</Box>
					)}
					{/* Action buttons when adjustor has already signed (no embed URL) */}
					{!openSignEmbedUrl && adjustorAlreadySigned && (
						<Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
							{!isTokenAccess && (
								<Button
									variant="contained"
									color="warning"
									onClick={() => navigate("/home")}
								>
									Back
								</Button>
							)}
							{showNotifyButton &&
								!signingStatus?.prematurelyCompleted && (
								<Button
									variant="contained"
									color="success"
									onClick={notifyUser}
									startIcon={<WhatsAppIcon />}
									disabled={isSubmitting}
								>
									Notify via WhatsApp
								</Button>
							)}
							{!isTokenAccess &&
								signingStatus?.prematurelyCompleted && (
								<Button
									variant="contained"
									color="error"
									disabled={isCreatingFromTemplate}
									onClick={
										reinitializeDocumentFromTemplate
									}
								>
									{isCreatingFromTemplate
										? "Recreating..."
										: "Recreate Acceptance Form"}
								</Button>
							)}
							{showSendForApproval && (
								<Button
									variant="contained"
									color="primary"
									onClick={downloadAndUpload}
									startIcon={<CloudDownloadIcon />}
									disabled={
										isDownloadingAndUploading ||
										!openSignDocumentId ||
										!signingStatus?.allSigned
									}
								>
									{isDownloadingAndUploading
										? "Processing..."
										: "Send for Approval"}
								</Button>
							)}
						</Box>
					)}
				</Box>
				{/* Assignment Details*/}
				{assignmentFromStore &&
					renderAssignmentDetails(assignmentFromStore)}

				{/* Document display */}
				{adjustorAlreadySigned && !openSignEmbedUrl ? (
					<Box
						sx={{
							p: 4,
							border: "1px solid #4caf50",
							borderRadius: 1,
							backgroundColor: "#f1f8e9",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h6" sx={{ color: "#2e7d32" }}>
								Document Signing Status
							</Typography>
							<Button
								size="small"
								variant="outlined"
								startIcon={<RefreshIcon />}
								onClick={handleRefreshStatus}
								disabled={isRefreshingStatus}
							>
								{isRefreshingStatus
									? "Refreshing..."
									: "Refresh"}
							</Button>
						</Box>

						{signingStatus && signingStatus.signers.length > 0 ? (
							<>
								<Box sx={{ mb: 2 }}>
									{signingStatus.signers.map(
										(signer, idx) => (
											<Box
												key={idx}
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent:
														"space-between",
													p: 1.5,
													mb: 1,
													bgcolor: "white",
													borderRadius: 1,
													border: "1px solid #e0e0e0",
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}
												>
													<Typography
														variant="body1"
														sx={{ fontWeight: 500 }}
													>
														{signer.Name}
													</Typography>
												</Box>
												<Chip
													label={
														signer.signed
															? "Signed"
															: "Pending"
													}
													color={
														signer.signed
															? "success"
															: "warning"
													}
													size="small"
													variant={
														signer.signed
															? "filled"
															: "outlined"
													}
												/>
											</Box>
										),
									)}
								</Box>

								{signingStatus.prematurelyCompleted ? (
									<Alert severity="error" sx={{ mb: 2 }}>
										OpenSign completed this document before
										every recipient signed. Recreate the
										Acceptance Form, then resend the WhatsApp
										links.
									</Alert>
								) : signingStatus.allSigned ? (
									<Alert severity="success" sx={{ mb: 2 }}>
										All parties have signed the document.
										You can now send it for approval.
									</Alert>
								) : (
									<Alert severity="info" sx={{ mb: 2 }}>
										Waiting for remaining parties to sign.
										Status refreshes automatically every 30
										seconds.
									</Alert>
								)}

								{signingStatus.lastRefreshed && (
									<Typography
										variant="caption"
										color="textSecondary"
									>
										Last updated:{" "}
										{signingStatus.lastRefreshed.toLocaleTimeString()}
									</Typography>
								)}
							</>
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								You have signed this document. Waiting for other
								parties to complete signing.
							</Typography>
						)}
					</Box>
				) : openSignEmbedUrl ? (
					<>
						<Box
							sx={{
								border: "1px solid #ddd",
								borderRadius: 1,
								height: "800px",
								overflow: "hidden",
								display: iframeError ? "none" : "block",
								backgroundColor: "#fff",
							}}
						>
							<iframe
								ref={iframeRef}
								title="Document Signing"
								src={openSignEmbedUrl}
								key={openSignDocumentId || openSignEmbedUrl}
								width="100%"
								height="100%"
								style={{
									border: "none",
									backgroundColor: "#fff",
								}}
								allow="clipboard-read; clipboard-write; fullscreen; display-capture; microphone; camera"
								referrerPolicy="no-referrer-when-downgrade"
								onLoad={handleIframeLoad}
								onError={handleIframeError}
							/>
						</Box>

						{/* Display a message and open in new window button when iframe fails */}
						{iframeError && (
							<Box
								sx={{
									p: 4,
									textAlign: "center",
									border: "1px dashed #ccc",
									borderRadius: 1,
								}}
							>
								<Typography variant="h6" sx={{ mb: 2 }}>
									There was an issue loading the document in
									this frame.
								</Typography>
								<Typography sx={{ mb: 3 }}>
									Please try opening the document in a new
									window instead.
								</Typography>
								<Button
									variant="contained"
									color="primary"
									component="a"
									href={openSignEmbedUrl}
									target="_blank"
								>
									Open Document in New Window
								</Button>
							</Box>
						)}
					</>
				) : (
					<Box
						sx={{
							p: 4,
							textAlign: "center",
							border: "1px dashed #ccc",
							borderRadius: 1,
						}}
					>
						<Typography>
							{localErrors.global ||
								(assignmentFromStore &&
								!assignmentFromStore.templateId
									? "No template assigned to this document."
									: "Unable to load document. Please try again later.")}
						</Typography>
						{effectiveUser?.userName &&
							/adjuster/i.test(effectiveUser?.role || "") && (
								<Box sx={{ mt: 2 }}>
									<Button
										variant="contained"
										color="primary"
										disabled={isCreatingFromTemplate}
										onClick={
											reinitializeDocumentFromTemplate
										}
									>
										{isCreatingFromTemplate
											? "Creating Acceptance Form..."
											: "Create Acceptance Form"}
									</Button>
								</Box>
							)}
					</Box>
				)}
			</Paper>
		</Container>
	);
};

export default AcceptanceForm;
